import CRMRoutes from "@/routes/crm-routes";

/**
 * Pathname only: strips hash and query (handles full asPath or pathname).
 */
export const pathnameOnly = (path) => {
  if (!path || typeof path !== "string") return "";
  const noHash = path.split("#")[0];
  return noHash.split("?")[0];
};

/** Pathname + query; strips hash only (handles full asPath or pathname). */
export const asPathWithoutHash = (path) => {
  if (!path || typeof path !== "string") return "";
  return path.split("#")[0];
};

/** Raw value from Redux before per-leaf migration (anchor/detail object). */
export const coerceRouteStoreValue = (v) => {
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v.anchor === "string") return v.anchor;
  return null;
};

/** Redux route-history key for a nav item. Permission `name` can differ (e.g. appointments dashboard). */
export const getNavLeafKey = (item) => item?.routeKey || item?.name || null;

const buildLeafRegistry = () => {
  const entries = [];
  for (const item of CRMRoutes) {
    if (item.childRoutes?.length) {
      for (const c of item.childRoutes) {
        if (c.path) entries.push({ path: c.path, name: getNavLeafKey(c) });
      }
    } else if (item.path) {
      entries.push({ path: item.path, name: getNavLeafKey(item) });
    }
  }
  entries.sort((a, b) => b.path.length - a.path.length);
  return entries;
};

const leafRegistry = buildLeafRegistry();

/** Longest CRM nav leaf path match → leaf `name` (Redux route key). */
export const getLeafNameForPathname = (pathnameOrAsPath) => {
  const pathname = pathnameOnly(pathnameOrAsPath);
  if (!pathname) return null;
  for (const { path, name } of leafRegistry) {
    if (pathname === path || pathname.startsWith(`${path}/`)) return name;
  }
  return null;
};

/** Registered base path for a leaf name (from crm-routes). */
export const getRegisteredPathForLeaf = (leafName) => {
  const e = leafRegistry.find((x) => x.name === leafName);
  return e?.path || null;
};

/** All registered base paths for a persistence key (`routeKey` or `name`). */
export const getRegisteredPathsForLeaf = (leafName) => {
  return leafRegistry.filter((x) => x.name === leafName).map((x) => x.path);
};

/**
 * If the stored value's pathname is under this leaf's registered base path, return the full stored string (including query); else null.
 * Keys are unique per leaf (`routeKey` or `name`), so analytics and appointments dashboards do not restore each other.
 */
export const sanitizePathForLeaf = (leafName, storedRaw) => {
  const stored = coerceRouteStoreValue(storedRaw);
  if (!stored || !leafName) return null;
  const prefixes = getRegisteredPathsForLeaf(leafName);
  if (!prefixes.length) return null;
  const path = pathnameOnly(stored);
  const matches = prefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
  return matches ? stored : null;
};
